import React, { FC, MouseEvent } from 'react';
import cx from 'classnames';
import { _t } from 'utils';

import iconArrow from 'assets/img/main-v4/icon-arrow.svg';

import Link from '../Link';
import styles from './styles.module.scss';

export type ButtonV4Variant = 'solid' | 'outline' | 'ghost';
export type ButtonV4Tone = 'primary' | 'onDark' | 'onLight' | 'frosted';
export type ButtonV4Size = 'md' | 'sm';
export type ButtonV4Radius = 'md' | 'sm';

type CommonProps = {
  label: string;
  variant?: ButtonV4Variant;
  tone?: ButtonV4Tone;
  size?: ButtonV4Size;
  radius?: ButtonV4Radius;
  className?: string;
  arrow?: boolean;
  iconSrc?: string;
  iconSize?: number;
};

type LinkProps = CommonProps & {
  to: string;
  onClick?: () => void;
  type?: never;
  disabled?: never;
};

type NativeProps = CommonProps & {
  to?: undefined;
  type?: 'button' | 'submit';
  onClick?: (event: MouseEvent<HTMLButtonElement>) => void;
  disabled?: boolean;
};

type Props = LinkProps | NativeProps;

const ButtonV4: FC<Props> = ({
  label,
  variant = 'solid',
  tone = 'primary',
  size = 'md',
  radius = 'md',
  className,
  arrow = true,
  iconSrc,
  iconSize = 12,
  to,
  type = 'button',
  onClick,
  disabled,
}) => {
  const rootClass = cx(
    styles.root,
    styles[variant],
    styles[tone],
    size === 'sm' && styles.sm,
    variant === 'outline' && tone === 'onDark' && radius === 'sm' && styles.radiusSm,
    className,
  );

  const content = (
    <>
      <span className={styles.label}>{_t(label)}</span>
      {arrow && (
        <span className={styles.arrow} aria-hidden>
          <img
            src={iconSrc || iconArrow}
            alt=""
            width={iconSize}
            height={iconSize}
          />
        </span>
      )}
    </>
  );

  if (to) {
    const isExternal = /^https?:\/\//i.test(to);

    if (isExternal) {
      return (
        <a
          href={to}
          className={rootClass}
          target="_blank"
          rel="noopener noreferrer"
          onClick={onClick as (() => void) | undefined}
        >
          {content}
        </a>
      );
    }

    return (
      <Link
        to={to}
        className={rootClass}
        onClick={onClick as (() => void) | undefined}
      >
        {content}
      </Link>
    );
  }

  return (
    // eslint-disable-next-line react/button-has-type
    <button
      type={type || 'button'}
      className={rootClass}
      onClick={onClick as ((event: MouseEvent<HTMLButtonElement>) => void) | undefined}
      disabled={disabled}
    >
      {content}
    </button>
  );
};

export default ButtonV4;
